/**
 * `JobPositioning` — pagina utente che mostra il posizionamento della persona
 * sulle professioni (e-CF 3.0).
 *
 * I Job non hanno assessment: il posizionamento è derivato dai match dello User
 * sulle competenze (vedi `GET /api/jobs/positioning/me`). Le card riusano lo
 * **stesso markup e lo stesso stile** delle `CompetencyMatchCard` della
 * Dashboard ("Le tue competenze"): donut della Copertura in alto a destra,
 * barra della Proficiency, footer con il conteggio, hover animato e click su
 * tutta la card. Unica differenza: niente accento sul bordo sinistro (specifico
 * delle card di dashboard), gestito dal modificatore `--job`.
 *
 * La lista è ordinata dall'API per `rankScore` decrescente. Empty state quando
 * non ci sono professioni configurate o l'utente non ha ancora valutazioni.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { JobPositioningDto } from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { JobMatchCard } from '../components/JobMatchCard';
import { JobScoreGuide } from '../components/JobScoreGuide';
import { Alert, EmptyState, Field, Loading, PageHeader, TextInput } from '../components/ui';

const positioningKey = ['jobs', 'positioning', 'me'] as const;

export default function JobPositioning() {
  const api = useApiClient();
  const [query, setQuery] = useState('');

  const positioningQuery = useQuery({
    queryKey: positioningKey,
    queryFn: () => api.get<JobPositioningDto>('/api/jobs/positioning/me'),
  });

  const jobMatches = useMemo(
    () => positioningQuery.data?.jobMatches ?? [],
    [positioningQuery.data],
  );

  // Ricerca client‑side per nome o sintesi (case‑insensitive, sottostringa),
  // coerente con le altre sezioni.
  const normalizedQuery = query.trim().toLowerCase();
  const visible = useMemo(() => {
    if (normalizedQuery === '') return jobMatches;
    return jobMatches.filter(
      (job) =>
        job.name.toLowerCase().includes(normalizedQuery) ||
        (job.summary ?? '').toLowerCase().includes(normalizedQuery),
    );
  }, [jobMatches, normalizedQuery]);

  return (
    <section className="job-positioning" aria-labelledby="job-positioning-title">
      <PageHeader
        title="Le mie professioni"
        titleId="job-positioning-title"
        subtitle="Il tuo posizionamento sulle professioni (e-CF 3.0), calcolato dalle competenze che possiedi."
        actions={<JobScoreGuide />}
      />

      {/* Ricerca a piena larghezza, stesso formato delle altre sezioni. */}
      <Field
        id="job-positioning-search"
        label="Cerca professione"
        hint="Filtra le professioni per nome o sintesi."
      >
        <div className="competence-search">
          <TextInput
            id="job-positioning-search"
            className="competence-search__input"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per nome o sintesi…"
            autoComplete="off"
            aria-label="Cerca professione per nome o sintesi"
          />
        </div>
      </Field>

      {positioningQuery.isError && (
        <Alert tone="error">Impossibile caricare il posizionamento. Riprova.</Alert>
      )}

      {positioningQuery.isLoading ? (
        <Loading>Calcolo del posizionamento…</Loading>
      ) : jobMatches.length === 0 ? (
        <EmptyState role="status">
          Nessuna professione disponibile: valuta alcune skill o chiedi all'amministratore
          di configurare le professioni.
        </EmptyState>
      ) : visible.length === 0 ? (
        <EmptyState role="status">
          Nessuna professione corrisponde alla ricerca.
        </EmptyState>
      ) : (
        <div className="job-positioning__list">
          {visible.map((job) => (
            <JobMatchCard key={job.jobId} job={job} />
          ))}
        </div>
      )}
    </section>
  );
}
